/**
 * Device Replacement History Card
 * แสดง "ประวัติการเปลี่ยนอุปกรณ์ของเครื่องนี้" ในหน้าจัดการอุปกรณ์
 *
 * ต่างจาก components/rental/DeviceReplacementHistory.tsx ตรงมุมมอง:
 * - ตัวนั้นมองจาก "สัญญา" (ทุกการเปลี่ยนในสัญญาเดียว)
 * - ตัวนี้มองจาก "เครื่อง" — เครื่องนี้เคยถูก **เปลี่ยนออก** (เป็นอุปกรณ์เดิม) หรือ
 *   **ถูกนำไปแทน** เครื่องอื่น (เป็นอุปกรณ์ใหม่) กี่ครั้ง
 *
 * backend: GET /device-replacements/devices/{id}/replacement-history (perm device_replacements.view)
 * — crud.get_by_device กรอง old_device_id OR new_device_id จึงคืนเฉพาะ DEVICE_ONLY/BOTH
 *   (SIM_ONLY เก็บ device id เป็น NULL ทั้งคู่ ไม่โผล่ที่นี่)
 *
 * ทิศทางของแต่ละแถวเทียบจาก "รหัสเครื่อง" (fallback S/N) เพราะ payload ของ endpoint นี้
 * ส่งกลับแค่ code/serial ไม่ได้ส่ง old_device_id/new_device_id
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  RiExchangeLine,
  RiArrowRightLine,
  RiTimeLine,
  RiExternalLinkLine,
  RiLoader4Line,
  RiErrorWarningLine,
} from 'react-icons/ri';
import deviceReplacementService, {
  DeviceReplacementHistoryItem,
  ReplacementType,
  REPLACEMENT_TYPES,
  DEVICE_CONDITIONS,
  SIM_CONDITIONS,
} from '../../services/deviceReplacementService';
import { Badge } from '../ui/StatusBadge';
import { usePermission } from '../../hooks/usePermission';
import { formatDateTimeFull } from '../../utils/dateUtils';

type BadgeVariant = NonNullable<React.ComponentProps<typeof Badge>['variant']>;

/**
 * field บริบทที่ endpoint ยังไม่ส่งกลับมา (schema DeviceReplacementHistoryItem ฝั่ง backend
 * มีแค่ code/serial/condition/reason/ผู้ทำ) — ประกาศเป็น optional ไว้ก่อน เพื่อให้ลิงก์
 * "ดูสัญญา / ดูเคสซ่อม" ขึ้นเองทันทีเมื่อ backend เพิ่ม field เหล่านี้ โดยไม่ต้องแก้ component อีก
 */
interface DeviceReplacementHistoryItemWithContext extends DeviceReplacementHistoryItem {
  rental_id?: number | null;
  rental_code?: string | null;
  site_name?: string | null;
  company_name?: string | null;
  maintenance_id?: number | null;
}

// สีของ badge เท่านั้น — ข้อความทั้งหมดอ่านจากค่าคงที่กลางใน deviceReplacementService
const REPLACEMENT_TYPE_VARIANT: Record<ReplacementType, BadgeVariant> = {
  DEVICE_ONLY: 'info',
  SIM_ONLY: 'success',
  BOTH: 'accent',
};

const DEVICE_CONDITION_VARIANT: Record<string, BadgeVariant> = {
  DAMAGED: 'danger',
  LOST: 'warning',
  MAINTENANCE: 'info',
};

const SIM_CONDITION_VARIANT: Record<string, BadgeVariant> = {
  SIM_EXPIRED: 'warning',
  SIM_FAULTY: 'danger',
  SIM_CHANGE_OPERATOR: 'accent',
  SIM_UPGRADE: 'info',
  SIM_DATA_ISSUE: 'warning',
  SIM_NORMAL_WITH_DEVICE: 'success',
};

/** อ่าน label จากค่าคงที่กลาง (REPLACEMENT_TYPES / DEVICE_CONDITIONS / SIM_CONDITIONS) */
const labelOf = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | null | undefined
): string | null => {
  if (!value) return null;
  return options.find(o => o.value === value)?.label ?? value;
};

/** เครื่องนี้อยู่ฝั่งไหนของการเปลี่ยน */
type ReplacementSide = 'OUT' | 'IN' | 'UNKNOWN';

const SIDE_META: Record<ReplacementSide, { label: string; variant: BadgeVariant; hint: string }> = {
  OUT: {
    label: 'เครื่องนี้ถูกเปลี่ยนออก',
    variant: 'warning',
    hint: 'เครื่องนี้เป็นอุปกรณ์เดิมที่ถูกถอดออกจากสัญญา',
  },
  IN: {
    label: 'เครื่องนี้ถูกนำไปแทน',
    variant: 'success',
    hint: 'เครื่องนี้เป็นอุปกรณ์ใหม่ที่ถูกส่งไปแทนเครื่องเดิม',
  },
  UNKNOWN: {
    label: 'เกี่ยวข้องกับเครื่องนี้',
    variant: 'secondary',
    hint: 'ระบุฝั่งของเครื่องไม่ได้จากข้อมูลที่บันทึกไว้',
  },
};

const sameIdentity = (a: string | null | undefined, b: string | null | undefined): boolean => {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
};

interface DeviceReplacementHistoryCardProps {
  /** id ของอุปกรณ์ที่กำลังดูรายละเอียด */
  deviceId: number;
  /** รหัสเครื่อง — ใช้เทียบว่าเครื่องนี้เป็นฝั่งเดิมหรือฝั่งใหม่ของแต่ละการเปลี่ยน */
  deviceCode?: string | null;
  /** S/N — ใช้เทียบสำรองเมื่อไม่มีรหัสเครื่อง */
  deviceSerial?: string | null;
  className?: string;
}

const DeviceReplacementHistoryCard: React.FC<DeviceReplacementHistoryCardProps> = ({
  deviceId,
  deviceCode,
  deviceSerial,
  className = '',
}) => {
  const { hasPermission, isSuperAdmin } = usePermission();
  const canView = isSuperAdmin || hasPermission('device_replacements.view');

  const [history, setHistory] = useState<DeviceReplacementHistoryItemWithContext[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!deviceId || !canView) return;

    setLoading(true);
    setError(null);
    try {
      const data = await deviceReplacementService.getDeviceReplacementHistory(deviceId);
      setHistory(data as DeviceReplacementHistoryItemWithContext[]);
    } catch (err) {
      console.error('Error loading device replacement history:', err);
      setError('ไม่สามารถโหลดประวัติการเปลี่ยนอุปกรณ์ของเครื่องนี้ได้');
    } finally {
      setLoading(false);
    }
  }, [deviceId, canView]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // ไม่มีสิทธิ์ = ไม่แสดงการ์ด (และไม่ยิง API ให้โดน 403)
  if (!canView) return null;

  const sideOf = (item: DeviceReplacementHistoryItemWithContext): ReplacementSide => {
    if (
      sameIdentity(item.old_device_code, deviceCode) ||
      sameIdentity(item.old_device_serial, deviceSerial)
    ) {
      return 'OUT';
    }
    if (
      sameIdentity(item.new_device_code, deviceCode) ||
      sameIdentity(item.new_device_serial, deviceSerial)
    ) {
      return 'IN';
    }
    return 'UNKNOWN';
  };

  const outCount = history.filter(item => sideOf(item) === 'OUT').length;
  const inCount = history.filter(item => sideOf(item) === 'IN').length;

  const renderShell = (children: React.ReactNode) => (
    <div className={`mt-4 p-4 border border-secondary-200 rounded-lg bg-white ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-secondary-200 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center shadow-sm">
            <RiExchangeLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">
            ประวัติการเปลี่ยนอุปกรณ์ของเครื่องนี้
          </h3>
          {history.length > 0 && (
            <span className="bg-secondary-100 text-secondary-600 text-xs px-2 py-0.5 rounded-full">
              {history.length}
            </span>
          )}
        </div>
        {history.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <Badge variant="warning" size="xs">เปลี่ยนออก {outCount} ครั้ง</Badge>
            <Badge variant="success" size="xs">นำไปแทน {inCount} ครั้ง</Badge>
          </div>
        )}
      </div>
      {children}
    </div>
  );

  if (loading) {
    return renderShell(
      <div className="flex items-center justify-center py-6 text-secondary-500">
        <RiLoader4Line className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">กำลังโหลดประวัติการเปลี่ยนอุปกรณ์...</span>
      </div>
    );
  }

  if (error) {
    return renderShell(
      <div className="flex flex-col items-center gap-3 py-6">
        <div className="flex items-center text-danger-600 text-sm">
          <RiErrorWarningLine className="w-5 h-5 mr-2" />
          {error}
        </div>
        <button type="button" className="btn btn-secondary px-3 py-1.5 text-sm" onClick={loadHistory}>
          ลองใหม่
        </button>
      </div>
    );
  }

  if (history.length === 0) {
    return renderShell(
      <div className="text-center py-6 text-secondary-500">
        <RiExchangeLine className="w-10 h-10 mx-auto text-secondary-300 mb-2" />
        <p className="text-sm">ยังไม่มีประวัติการเปลี่ยนอุปกรณ์</p>
      </div>
    );
  }

  return renderShell(
    <div className="space-y-3">
      {history.map(item => {
        const side = sideOf(item);
        const sideMeta = SIDE_META[side];
        const replacementType: ReplacementType = item.replacement_type || 'DEVICE_ONLY';
        const typeLabel = labelOf(REPLACEMENT_TYPES, replacementType) || replacementType;
        const deviceConditionLabel = labelOf(DEVICE_CONDITIONS, item.old_device_condition);
        const simConditionLabel = labelOf(SIM_CONDITIONS, item.old_sim_condition);
        const showSimSection =
          replacementType === 'SIM_ONLY' || replacementType === 'BOTH' ||
          Boolean(item.old_sim_number || item.new_sim_number);

        return (
          <div
            key={item.id}
            className="border border-secondary-200 rounded-lg p-3 hover:bg-secondary-50 transition-colors"
          >
            {/* หัวแถว: วันที่ + ประเภทการเปลี่ยน + ฝั่งของเครื่องนี้ + สาเหตุ */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center text-xs text-secondary-500">
                  <RiTimeLine className="w-3.5 h-3.5 mr-1" />
                  {formatDateTimeFull(item.replacement_date)}
                </span>
                <Badge variant={REPLACEMENT_TYPE_VARIANT[replacementType] || 'secondary'} size="xs">
                  {typeLabel}
                </Badge>
                <span title={sideMeta.hint}>
                  <Badge variant={sideMeta.variant} size="xs">{sideMeta.label}</Badge>
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {deviceConditionLabel && (
                  <Badge
                    variant={DEVICE_CONDITION_VARIANT[item.old_device_condition || ''] || 'secondary'}
                    size="xs"
                  >
                    สภาพเครื่องเดิม: {deviceConditionLabel}
                  </Badge>
                )}
                {simConditionLabel && (
                  <Badge
                    variant={SIM_CONDITION_VARIANT[item.old_sim_condition || ''] || 'secondary'}
                    size="xs"
                  >
                    {simConditionLabel}
                  </Badge>
                )}
              </div>
            </div>

            {/* เครื่องเดิม → เครื่องใหม่ (ฝั่งที่เป็นเครื่องนี้เน้นสี) */}
            <div className="flex items-center gap-2 text-sm">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-secondary-500">อุปกรณ์เดิม</p>
                <p
                  className={`font-medium truncate ${
                    side === 'OUT' ? 'text-warning-700' : 'text-secondary-900'
                  }`}
                >
                  {item.old_device_code || 'ไม่ระบุ'}
                  {side === 'OUT' && <span className="ml-1 text-xs font-normal">(เครื่องนี้)</span>}
                </p>
                {item.old_device_serial && (
                  <p className="text-xs text-secondary-500 truncate">S/N: {item.old_device_serial}</p>
                )}
              </div>

              <RiArrowRightLine className="w-5 h-5 text-secondary-400 shrink-0" />

              <div className="flex-1 min-w-0">
                <p className="text-xs text-secondary-500">อุปกรณ์ใหม่</p>
                <p
                  className={`font-medium truncate ${
                    side === 'IN' ? 'text-success-700' : 'text-secondary-900'
                  }`}
                >
                  {item.new_device_code || 'ไม่ระบุ'}
                  {side === 'IN' && <span className="ml-1 text-xs font-normal">(เครื่องนี้)</span>}
                </p>
                {item.new_device_serial && (
                  <p className="text-xs text-secondary-500 truncate">S/N: {item.new_device_serial}</p>
                )}
              </div>
            </div>

            {/* ซิม (เปลี่ยนพร้อมเครื่อง) */}
            {showSimSection && (
              <div className="flex items-center gap-2 mt-2 bg-secondary-50 rounded-md p-2 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-secondary-500">SIM เดิม</p>
                  <p className="text-secondary-700 truncate">{item.old_sim_number || '-'}</p>
                </div>
                <RiArrowRightLine className="w-4 h-4 text-secondary-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-secondary-500">SIM ใหม่</p>
                  <p className="text-success-700 truncate">{item.new_sim_number || '-'}</p>
                </div>
              </div>
            )}

            {/* AnyDesk (แสดงเมื่อมีค่าฝั่งใดฝั่งหนึ่ง) */}
            {(item.old_anydesk_id || item.new_anydesk_id) && (
              <div className="flex items-center gap-2 mt-2 text-xs text-secondary-600">
                <span className="text-secondary-500">AnyDesk:</span>
                <span className="font-mono">{item.old_anydesk_id || '-'}</span>
                <RiArrowRightLine className="w-3.5 h-3.5 text-secondary-400" />
                {item.new_anydesk_id ? (
                  <span className="font-mono text-success-700">{item.new_anydesk_id}</span>
                ) : (
                  <span className="italic text-secondary-400">ล้างค่า</span>
                )}
              </div>
            )}

            {/* เหตุผล / หมายเหตุ */}
            <div className="mt-2 text-sm break-words">
              <span className="text-secondary-500">เหตุผล: </span>
              <span className="text-secondary-700">{item.reason || '-'}</span>
            </div>
            {item.notes && (
              <div className="mt-1 text-sm break-words">
                <span className="text-secondary-500">หมายเหตุ: </span>
                <span className="text-secondary-600 italic">{item.notes}</span>
              </div>
            )}

            {/* ท้ายแถว: ผู้ทำรายการ + สัญญา/โครงการ + ลิงก์ */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-2 pt-2 border-t border-secondary-100 text-xs text-secondary-500">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>โดย: {item.replaced_by_name || 'ไม่ระบุ'}</span>
                {(item.company_name || item.site_name) && (
                  <span>
                    โครงการ: {item.site_name || '-'}
                    {item.company_name ? ` · ${item.company_name}` : ''}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {item.rental_id && (
                  <a
                    href={`/rentals/view/${item.rental_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    <RiExternalLinkLine className="w-3.5 h-3.5 mr-1" />
                    ดูสัญญา{item.rental_code ? ` ${item.rental_code}` : ''}
                  </a>
                )}
                {item.maintenance_id && (
                  <a
                    href={`/maintenance/view/${item.maintenance_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    <RiExternalLinkLine className="w-3.5 h-3.5 mr-1" />
                    ดูเคสซ่อม #{item.maintenance_id}
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DeviceReplacementHistoryCard;
